import { useOidcAccessToken } from '@axa-fr/react-oidc';
import axios from 'axios';
import { ReactNode } from 'react';
import { i18n } from '../../i18n';
import {
  BackfillRecipesService,
  BinderCostRecordsService,
  BinderDeliveriesService,
  BinderSuppliersService,
  BreakingSchedulesService,
  BreakingTestsService,
  ClientsService,
  CustomReportsService,
  FlushRecipesService,
  HistoricBreakingTestService,
  InvitationsService,
  MineModelsService,
  NotificationsService,
  PoursService,
  PourTypesService,
  ProjectsService,
  PublicPoursService,
  PublicStopeNotesService,
  QaqcSamplesService,
  ScenariosService,
  Service,
  StopeBackfillingService,
  StopeNotesService,
  TeamRolesService,
  TeamsService,
  UserDashboardsService,
  UserNotificationsService,
  UsersService
} from './api';
import { AccountAdminService } from './accountAdminApi';
import { AnalyticsService } from './analyticsApi';
import ApiContext, { IApiContext } from './context';

export interface ApiProviderProps {
  children: ReactNode;
}

export default function ApiProvider({ children }: ApiProviderProps) {
  const { accessToken } = useOidcAccessToken();

  const instance = axios.create({
    headers: { Authorization: `Bearer ${accessToken}` },
    transformResponse: (res) => res,
  });

  // The backend renders generated reports (pour/stope notes) in the caller's UI language,
  // read from Accept-Language. Resolved per request so a locale switch takes effect immediately.
  instance.interceptors.request.use((config) => ({
    ...config,
    headers: { ...config.headers, 'Accept-Language': i18n.locale || 'en' },
  }));

  const clients = new ClientsService(window.location.origin, instance);
  const projects = new ProjectsService(window.location.origin, instance);
  const users = new UsersService(window.location.origin, instance);
  const invitations = new InvitationsService(window.location.origin, instance);
  const teams = new TeamsService(window.location.origin, instance);
  const mineModels = new MineModelsService(window.location.origin, instance);
  const recipes = new BackfillRecipesService(window.location.origin, instance);
  const flushRecipes = new FlushRecipesService(window.location.origin, instance);
  const pours = new PoursService(window.location.origin, instance);
  const teamRoles = new TeamRolesService(window.location.origin, instance);
  const stopes = new StopeBackfillingService(window.location.origin, instance);
  const pourTypes = new PourTypesService(window.location.origin, instance);
  const stopeNotes = new StopeNotesService(window.location.origin, instance);
  const contentApi = new Service(window.location.origin, instance);
  const binderCostRecords = new BinderCostRecordsService(window.location.origin, instance);
  const binderDeliveries = new BinderDeliveriesService(window.location.origin, instance);
  const binderSuppliers = new BinderSuppliersService(window.location.origin, instance);
  const userNotifications = new UserNotificationsService(window.location.origin, instance);
  const notifications = new NotificationsService(window.location.origin, instance);
  const breakingSchedules = new BreakingSchedulesService(window.location.origin, instance);
  const breakingTests = new BreakingTestsService(window.location.origin, instance);
  const publicPours = new PublicPoursService(window.location.origin, instance);
  const publicStopeNotes = new PublicStopeNotesService(window.location.origin, instance);
  const historicBreakingTests = new HistoricBreakingTestService(window.location.origin, instance);
  const scenarios = new ScenariosService(window.location.origin, instance);
  const customReports = new CustomReportsService(window.location.origin, instance);
  const qaqcSamples = new QaqcSamplesService(window.location.origin, instance);
  const analytics = new AnalyticsService(window.location.origin, instance);
  const userDashboards = new UserDashboardsService(window.location.origin, instance);
  const accountAdmin = new AccountAdminService(window.location.origin, instance);

  const value: IApiContext = {
    clients,
    projects,
    users,
    teams,
    invitations,
    mineModels,
    recipes,
    flushRecipes,
    pours,
    teamRoles,
    stopes,
    pourTypes,
    stopeNotes,
    contentApi,
    binderCostRecords,
    binderDeliveries,
    binderSuppliers,
    userNotifications,
    notifications,
    breakingSchedules,
    breakingTests,
    historicBreakingTests,
    publicPours,
    publicStopeNotes,
    scenarios,
    customReports,
    qaqcSamples,
    analytics,
    userDashboards,
    accountAdmin,
  };

  return (
    <ApiContext.Provider value={value}>
      {children}
    </ApiContext.Provider>
  );
}
